import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api/client.js';
import { useExtensions } from '../../../lib/api/hooks.js';
import {
  useBusinessHours, useCallerChecks, useHoursChecks, useInboundRoutes, useIvrs, useLookups, useMailboxes, useMessages, useQueues,
  useRingGroups, useSchedules,
} from '../../../lib/api/hooks-tms.js';
import type { Live } from './model.js';

/**
 * Everything an account's routing is made of, live, with the names its steps refer to: what the flow
 * editor draws and what version history words its changes with. `ready` once the routes are in.
 */
export function useLiveRouting(tenantId: string): { live: Live; ready: boolean } {
  const routesQ = useInboundRoutes(tenantId);
  const ivrsQ = useIvrs(tenantId);
  const groupsQ = useRingGroups(tenantId);
  const queuesQ = useQueues(tenantId);
  const messagesQ = useMessages(tenantId);
  const hoursQ = useHoursChecks(tenantId);
  const callersQ = useCallerChecks(tenantId);
  const lookupsQ = useLookups(tenantId);
  const extQ = useExtensions(tenantId, { limit: 1000 });
  const boxesQ = useMailboxes(tenantId);
  const schedulesQ = useSchedules(tenantId);
  const businessQ = useBusinessHours(tenantId);
  const mediaQ = useQuery({
    queryKey: ['media', tenantId],
    queryFn: () => api.get<{ data: Array<{ id: string; kind: string; name: string; duration_sec: number | null }> }>(`/v1/tenants/${tenantId}/media`),
    enabled: !!tenantId,
  });

  const live: Live = useMemo(() => ({
    routes: routesQ.data?.data ?? [], ivrs: ivrsQ.data?.data ?? [], groups: groupsQ.data?.data ?? [],
    queues: queuesQ.data?.data ?? [], messages: messagesQ.data?.data ?? [], hours: hoursQ.data?.data ?? [],
    callers: callersQ.data?.data ?? [], lookups: lookupsQ.data?.data ?? [],
    extensions: extQ.data?.data ?? [], boxes: boxesQ.data?.data ?? [], schedules: schedulesQ.data?.data ?? [],
    numbers: [
      ...(routesQ.data?.data ?? []).flatMap((r) => r.numbers.map((n) => ({ id: n.id, e164: n.e164, label: n.label ?? null }))),
      ...(routesQ.data?.unrouted ?? []).map((n) => ({ id: n.id, e164: n.e164, label: n.label ?? null })),
    ],
    media: mediaQ.data?.data ?? [], businessId: businessQ.data?.schedule_id ?? null,
  }), [routesQ.data, ivrsQ.data, groupsQ.data, queuesQ.data, messagesQ.data, hoursQ.data, callersQ.data, lookupsQ.data, extQ.data, boxesQ.data,
    schedulesQ.data, mediaQ.data, businessQ.data]);

  return { live, ready: routesQ.data !== undefined };
}
